import React from 'react';
import { Typography } from '../../../typography';
import { Table } from '../../../table';
import { InformationCardProps } from '../../InformationCard.types';

/**
 * `CardTable` Component
 *
 * This component renders a table using the provided `data` prop in the `tableInformation` field.
 * It displays rows and columns based on the configuration provided, and shows a message if no data is available.
 *
 * @remarks
 * - Uses a `Table` component to render the table rows and columns.
 * - Displays a message ("No data to display") when there is no data.
 * - Throws an error if `tableInformation` is missing or incomplete in the `data` prop.
 *
 * @throws Will throw an error if `props.data.tableInformation` is not provided.
 * @param {InformationCardProps} props - The properties passed to the `CardTable` component.
 * @returns {JSX.Element} A table displaying the provided data or a message if no data is available.
 *
 * @example
 * ```tsx
 * import CardTable from './CardTable';
 *
 * const data = {
 *   tableInformation: {
 *     rows: [
 *       { name: 'Row 1', value: 'Data 1' },
 *       { name: 'Row 2', value: 'Data 2' },
 *     ],
 *     columns: [
 *       { title: 'Name', field: 'name' },
 *       { title: 'Value', field: 'value' },
 *     ],
 *     iconMap: {
 *       name: 'icon-for-name',
 *       value: 'icon-for-value',
 *     },
 *   },
 * };
 *
 * export const App = () => <CardTable data={data} />;
 * ```
 *
 * @category InformationCard
 * @document CardTableComponentUsage.md
 */
export const CardTable = (props: InformationCardProps): JSX.Element => {
  // Extract table configuration from props
  const config = props.data?.tableInformation;

  if (config) {
    return (
      <Table
        containerProps={{}}
        data={config.rows}
        columns={config.columns}
        iconMap={config.iconMap}
        EmptyComponent={<Typography>No data to display</Typography>}
      />
    );
  } else {
    // Throw an error if required data is missing
    throw new Error('CardTable component requires data prop to be passed');
  }
};
